import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { driverApi } from '../services/driverApi';

interface UpdateVehicleParams {
  vehicleId: string;
  updateData: {
    vehicleName?: string;
    vehicleModel?: string;
    vehicleYear?: number;
    licensePlate?: string;
    vin?: string;
    odometer?: number;
    vehicleAttributes?: any;
  };
}

interface UpdateVehicleResponse {
  success: boolean;
  message: string;
  vehicle: any;
}

const updateVehicle = async (
  token: string,
  { vehicleId, updateData }: UpdateVehicleParams
): Promise<UpdateVehicleResponse> => {
  console.log('🔧 [useUpdateVehicle] Calling updateVehicle with:', { vehicleId, updateData });
  driverApi.setAuthToken(token);
  const result = await driverApi.updateVehicle(vehicleId, updateData);
  console.log('🔧 [useUpdateVehicle] API response:', result);
  return result;
};

export const useUpdateVehicle = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const queryClient = useQueryClient();

  return useMutation<UpdateVehicleResponse, Error, UpdateVehicleParams>({
    mutationFn: async ({ vehicleId, updateData }: UpdateVehicleParams) => {
      const token = await getAccessTokenSilently();
      return updateVehicle(token, { vehicleId, updateData });
    },
    onSuccess: (data, variables) => {
      console.log('🔄 [useUpdateVehicle] Invalidating queries after successful update');
      
      // Get the vehicleId from the mutation variables
      const { vehicleId } = variables;
      
      // Find all driver-call queries that contain this vehicle
      const driverCallQueries = queryClient.getQueriesData({ queryKey: ['driver-call'] });
      driverCallQueries.forEach(([queryKey, queryData]: [unknown, any]) => {
        if (queryData?.vehicle?.id === vehicleId) {
          console.log('🔄 [useUpdateVehicle] Found matching driver-call query to invalidate:', queryKey);
          queryClient.invalidateQueries({ queryKey: queryKey as any });
        }
      });

      // Find all driver-impound queries and invalidate them
      const driverImpoundQueries = queryClient.getQueriesData({ queryKey: ['driver-impound'] });
      driverImpoundQueries.forEach(([queryKey, queryData]: [unknown, any]) => {
        if (queryData?.dispatch?.vehicle?.id === vehicleId) {
          console.log('🔄 [useUpdateVehicle] Found matching driver-impound query to invalidate:', queryKey);
          queryClient.invalidateQueries({ queryKey: queryKey as any });
        }
      });

      // Invalidate the driver-calls list
      queryClient.invalidateQueries({ queryKey: ['driver-calls'] });
      
      // Invalidate the driver-impounds list
      queryClient.invalidateQueries({ queryKey: ['driver-impounds'] });
      
      // Update the cache directly with the new vehicle data for driver-call queries
      queryClient.setQueriesData({ queryKey: ['driver-call'] }, (oldData: any) => {
        if (oldData?.vehicle?.id === vehicleId) {
          return {
            ...oldData,
            vehicle: data.vehicle
          };
        }
        return oldData;
      });

      // Update the cache directly with the new vehicle data for driver-impound queries
      queryClient.setQueriesData({ queryKey: ['driver-impound'] }, (oldData: any) => {
        if (oldData?.dispatch?.vehicle?.id === vehicleId) {
          return {
            ...oldData,
            dispatch: {
              ...oldData.dispatch,
              vehicle: data.vehicle
            }
          };
        }
        return oldData;
      });
    },
  });
}; 